import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDashboardStore } from '@/state/useDashboardStore'
import { useSettingsStore } from '@/state/useSettingsStore'
import { OsmMap, type MapPoint } from '@/components/map/OsmMap'

export function MapSlide() {
  const { t } = useTranslation()
  const snapshot = useDashboardStore((s) => s.snapshot)
  const locale = useSettingsStore((s) => s.locale)

  const points = useMemo<MapPoint[]>(() => {
    if (!snapshot) return []
    return snapshot.venueMap.map((v) => ({
      id: v.id,
      name: v.name,
      city: v.city,
      lat: v.lat,
      lng: v.lng,
      // Zelená = jede. Červená = jela a utichla (ztracený zákazník, nejhorší
      // případ). Žlutá = zatím se nerozjela, což je nejspíš jen onboarding.
      colorVar: v.active7d ? 'signal-good' : v.everActive ? 'signal-bad' : 'signal-warn',
      radius: 9,
    }))
  }, [snapshot])

  if (!snapshot) return null

  const cs = locale === 'cs'
  const clients = snapshot.clients

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {/* Jedna řada: kolik jich je, kolik jich žije a kolik ne. */}
      <div className="grid grid-cols-3 gap-4">
        <SummaryTile
          value={clients.total}
          label={cs ? 'Klientů celkem' : 'Clients total'}
          colorVar="brand-primary"
        />
        <SummaryTile
          value={clients.active7d}
          label={cs ? 'Aktivních za 7 dní' : 'Active last 7 days'}
          colorVar="signal-good"
        />
        <SummaryTile
          value={clients.inactive7d}
          label={cs ? 'Neaktivní klienti' : 'Inactive clients'}
          colorVar="signal-bad"
        />
      </div>

      <section className="relative min-h-0 flex-1 overflow-hidden rounded-3xl border border-border bg-surface-1">
        <OsmMap points={points} className="absolute inset-0 h-full w-full" />
        {snapshot.venueMapUnplaced > 0 && (
          <div className="pointer-events-none absolute bottom-3 left-3 z-[500] rounded-xl border border-border bg-surface-1/90 px-3 py-1.5 text-xs text-ink-subtle">
            {cs
              ? `${snapshot.venueMapUnplaced} poboček bez souřadnic`
              : `${snapshot.venueMapUnplaced} venues without coordinates`}
          </div>
        )}
      </section>
      <span className="sr-only">{t('slides.map')}</span>
    </div>
  )
}

function SummaryTile({
  value,
  label,
  colorVar,
  hint,
}: {
  value: number | null
  label: string
  colorVar: string
  hint?: string
}) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface-1/70 px-5 py-3">
      <span
        className="size-3 shrink-0 rounded-full"
        style={{ backgroundColor: `var(--${colorVar})` }}
      />
      <div className="min-w-0">
        <div className="font-display text-2xl font-semibold text-ink">
          {value == null ? '—' : value}
        </div>
        <div className="truncate text-xs uppercase tracking-[0.14em] text-ink-subtle">{label}</div>
        {value == null && hint && (
          <div className="truncate text-[10px] text-ink-subtle/70">{hint}</div>
        )}
      </div>
    </div>
  )
}
